<template>
    <section class="member-directory" aria-labelledby="member-directory-title">
        <div class="member-toolbar">
            <div class="member-search">
                <label class="sr-only" for="member-search">Search members</label>
                <input
                    id="member-search"
                    v-model="dataFilter.searchText"
                    type="search"
                    class="form-control"
                    placeholder="Search members"
                    @keyup.enter="onSearch();"
                >
                <button class="btn btn-outline-primary" type="button" @click="onSearch();" :disabled="loading">
                    Search
                </button>
            </div>

            <div class="member-toolbar-actions">
                <router-link to="/member/add" class="btn btn-outline-primary">
                    <i class="pi pi-user-plus" aria-hidden="true"></i>
                    <span>Add Member</span>
                </router-link>
                <button class="btn btn-primary sync-button" type="button" @click="onReviewSync();">
                    <i class="pi pi-sync" aria-hidden="true"></i>
                    <span>Review &amp; Sync</span>
                </button>
            </div>
        </div>

        <div class="member-list-meta" aria-live="polite">
            <span v-if="loading">Loading member directory…</span>
            <span v-else>{{ resultSummary }}</span>
        </div>

        <div class="member-table-wrap">
            <table class="table member-table">
                <thead>
                <tr>
                    <th scope="col" class="order-column">No.</th>
                    <th scope="col">Member</th>
                    <th scope="col">Login ID</th>
                    <th scope="col">Organization</th>
                    <th scope="col">Contact</th>
                    <th scope="col">City</th>
                    <th scope="col">Merchandise</th>
                    <th scope="col" class="actions-column"><span class="sr-only">Actions</span></th>
                </tr>
                </thead>

                <tbody>
                <tr v-if="loading">
                    <td colspan="8">
                        <div class="member-table-state">
                            <i class="pi pi-spinner is-spinning" aria-hidden="true"></i>
                            <span>Loading members…</span>
                        </div>
                    </td>
                </tr>

                <tr v-else-if="dataList.length === 0">
                    <td colspan="8">
                        <div class="member-table-state member-table-state--empty">
                            <i class="pi pi-users" aria-hidden="true"></i>
                            <strong>No members found</strong>
                            <span>{{ emptyStateMessage }}</span>
                        </div>
                    </td>
                </tr>

                <tr v-for="(data, index) in dataList" :key="data.id">
                    <td class="order-number">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                    <td class="member-name">{{ displayValue(data.name) }}</td>
                    <td><code>{{ displayValue(data.username) }}</code></td>
                    <td>{{ displayValue(data.organizationName) }}</td>
                    <td class="member-contact">
                        <a v-if="data.email" :href="`mailto:${data.email}`">{{ data.email }}</a>
                        <span v-else>—</span>
                        <small>{{ displayValue(data.phoneNumber) }}</small>
                    </td>
                    <td>{{ displayValue(data.cityName) }}</td>
                    <td>
                        <span v-if="data.merchandiseClaimedAt != null" class="claim-status">
                            <i class="pi pi-check-circle" aria-hidden="true"></i>
                            <span>Claimed <small>{{ fmtDate(data.merchandiseClaimedAt) }}</small></span>
                        </span>
                        <button v-else type="button" class="btn btn-claim" @click="onClaim(data)">
                            <i class="pi pi-check" aria-hidden="true"></i>
                            <span>Claim</span>
                        </button>
                    </td>
                    <td class="actions">
                        <button
                            type="button"
                            class="action-button"
                            v-tooltip="'Resend account information'"
                            :aria-label="`Resend account information to ${displayValue(data.name)}`"
                            :disabled="sendingAccountInformation"
                            @click="onResendAccount(data)"
                        ><i class="pi pi-envelope" aria-hidden="true"></i></button>
                        <button
                            type="button"
                            class="action-button"
                            v-tooltip="'Edit member'"
                            :aria-label="`Edit ${displayValue(data.name)}`"
                            @click="onEdit(data)"
                        ><i class="pi pi-pencil" aria-hidden="true"></i></button>
                        <button
                            type="button"
                            class="action-button action-button--danger"
                            v-tooltip="'Remove member'"
                            :aria-label="`Remove ${displayValue(data.name)}`"
                            @click="onRemove(data)"
                        ><i class="pi pi-trash" aria-hidden="true"></i></button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="member-pagination" v-if="totalRecords > 0 && !loading">
            <p>{{ paginationSummary }}</p>
            <Paginator
                :rows="pageSize"
                :totalRecords="totalRecords"
                :first="(currentPage - 1) * pageSize"
                @page="onPageChange"
                template="PrevPageLink PageLinks NextPageLink"
            />
        </div>

        <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import {defineComponent} from 'vue'
import apiService from "@/services/ApiService";
import moment from "moment";

export default defineComponent({
    name: "MemberList",
    data() {
        return {
            dataList: [],
            dataFilter: {
                searchText: ''
            },
            sendingAccountInformation: false,
            currentPage: 1,
            pageSize: 10,
            totalRecords: 0,
            totalPages: 0,
            loading: false
        }
    },
    computed: {
        resultSummary() {
            if (this.totalRecords === 0) {
                return 'No member records to display';
            }

            return `${this.totalRecords} member${this.totalRecords === 1 ? '' : 's'} found`;
        },
        paginationSummary() {
            const start = (this.currentPage - 1) * this.pageSize + 1;
            const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);
            return `Showing ${start}–${end} of ${this.totalRecords} members`;
        },
        emptyStateMessage() {
            return this.dataFilter.searchText
                ? 'Try a different name, login ID, email, or organization.'
                : 'Member records will appear here once they are available.';
        }
    },
    methods: {
        displayValue(value) {
            return value || '—';
        },
        onEdit(data) {
            this.$router.push(`/member/edit/${data.id}`);
        },
        onRemove(data) {
            this.$confirm.require({
                message: 'Are you sure you want to remove this member?',
                header: 'Remove member',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.doRemove(data.id);
                }
            });
        },
        onSearch() {
            this.resetPagination();
            this.doFind();
        },
        doFind() {
            this.loading = true;
            const params = {
                searchText: this.dataFilter.searchText || '',
                start: (this.currentPage - 1) * this.pageSize,
                length: this.pageSize
            };
            apiService.get('/member/datatable', {params: params})
                .then(response => {
                    this.dataList = response.data.data || [];
                    this.totalRecords = response.data.recordsFiltered || response.data.recordsTotal || 0;
                    this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to get member data',
                        detail: error.response?.data?.Message || 'An error occurred',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        doRemove(id) {
            apiService.delete(`/member/${id}`)
                .then(() => {
                    this.doFind();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Member has been removed',
                        life: 5000
                    });
                }).catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to remove member',
                        detail: error.response.data.Message,
                        life: 5000
                    });
                });
        },
        onClaim(data) {
            apiService.post(`/member/${data.id}/claim-merchandise`)
                .then(() => {
                    this.doFind();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Member has been claimed merchandise',
                        life: 5000
                    });
                }).catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to claim merchandise',
                        detail: error.response.data.Message,
                        life: 5000
                    });
                });
        },
        onReviewSync() {
            this.$router.push('/member/sync');
        },
        doResendAccount(data) {
            this.sendingAccountInformation = true;

            apiService.post('/member/resendAccount', [data.id])
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `The account information has been sent to ${data.email}`,
                        life: 5000
                    });
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to send account information',
                        detail: error.response.data.Message,
                        life: 5000
                    });
                })
                .finally(() => {
                    this.sendingAccountInformation = false;
                });
        },
        onResendAccount(data) {
            this.$confirm.require({
                message: `Resend account information to ${data.email}?`,
                header: 'Resend account information',
                icon: 'pi pi-envelope',
                accept: () => {
                    this.doResendAccount(data);
                }
            });
        },
        onPageChange(event) {
            this.currentPage = event.page + 1;
            this.pageSize = event.rows;
            this.doFind();
        },
        resetPagination() {
            this.currentPage = 1;
        },
        fmtDate(date) {
            return moment(date).format('DD/MM/YYYY HH:mm:ss');
        }
    },
    mounted() {
        this.dataList = [];
        this.doFind();
    }
})
</script>

<style scoped>
.member-directory { overflow: hidden; border: 1px solid #e5e7eb; border-radius: 0.75rem; background: #fff; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); }
.member-toolbar { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; padding: 1.25rem 1.25rem 1rem; border-bottom: 1px solid #edf0f5; }
.member-search { display: flex; flex: 1; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
.member-search .form-control { max-width: 19rem; }
.member-toolbar-actions, .actions { display: flex; align-items: center; gap: 0.5rem; }
.sync-button { display: inline-flex; min-height: 2.65rem; align-items: center; justify-content: center; gap: 0.45rem; padding-right: 0.9rem; padding-left: 0.9rem; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 600; }
.member-list-meta { min-height: 2.75rem; padding: 0.85rem 1.25rem; color: #6b7280; font-size: 0.8125rem; }
.member-table-wrap { overflow-x: auto; border-top: 1px solid #edf0f5; }
.member-table { min-width: 68rem; margin: 0; color: #374151; font-size: 0.875rem; }
.member-table thead th { padding: 0.8rem 1rem; border-top: 0; border-bottom: 1px solid #e5e7eb; background: #f8fafc; color: #6b7280; font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.045em; text-transform: uppercase; white-space: nowrap; }
.member-table tbody td { padding: 0.95rem 1rem; border-color: #edf0f5; vertical-align: middle; }
.member-table tbody tr:hover { background: #fafbff; }
.order-column, .order-number { width: 4.25rem; text-align: center; }
.order-number { color: #9ca3af; font-variant-numeric: tabular-nums; }
.member-name { min-width: 10rem; color: #1f2937; font-weight: 700; }
.member-table code { padding: 0; background: transparent; color: #4b5563; font-family: inherit; font-size: inherit; }
.member-contact { min-width: 13rem; }
.member-contact a { display: block; overflow: hidden; color: #3656bf; text-overflow: ellipsis; white-space: nowrap; }
.member-contact small, .claim-status small { display: block; margin-top: 0.2rem; color: #9ca3af; font-size: 0.75rem; }
.claim-status { display: inline-flex; align-items: flex-start; gap: 0.45rem; color: #166534; font-size: 0.8125rem; font-weight: 600; white-space: nowrap; }
.claim-status > i { margin-top: 0.1rem; color: #16a34a; }
.btn-claim { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.38rem 0.65rem; border: 1px solid #b7d7c0; border-radius: 0.375rem; background: #f0fdf4; color: #166534; font-size: 0.8125rem; font-weight: 600; }
.btn-claim:hover, .btn-claim:focus { border-color: #86bf95; background: #dcfce7; color: #14532d; }
.actions-column, .actions { width: 8.5rem; white-space: nowrap; }
.action-button { display: inline-flex; width: 2rem; height: 2rem; align-items: center; justify-content: center; border: 1px solid #d7dce5; border-radius: 0.375rem; background: #fff; color: #4b5563; transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease; }
.action-button:hover, .action-button:focus { border-color: #9eaceb; background: #f1f4ff; color: #3656bf; }
.action-button--danger:hover, .action-button--danger:focus { border-color: #f5b5b5; background: #fff1f1; color: #b42318; }
.action-button:disabled { cursor: not-allowed; opacity: 0.5; }
.member-table-state { display: flex; min-height: 9rem; align-items: center; justify-content: center; gap: 0.6rem; color: #6b7280; }
.member-table-state--empty { flex-direction: column; gap: 0.35rem; text-align: center; }
.member-table-state--empty > i { margin-bottom: 0.25rem; color: #9ca3af; font-size: 1.5rem; }
.member-table-state--empty strong { color: #374151; font-size: 0.95rem; }
.member-table-state--empty span { font-size: 0.8125rem; }
.member-pagination { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem 1.25rem; border-top: 1px solid #edf0f5; }
.member-pagination p { margin: 0; color: #6b7280; font-size: 0.8125rem; }
.is-spinning { animation: member-spin 0.9s linear infinite; }
@keyframes member-spin { to { transform: rotate(360deg); } }
@media (max-width: 767.98px) { .member-toolbar { flex-direction: column; align-items: stretch; } .member-search .form-control { max-width: none; width: 100%; } .member-toolbar-actions .btn { flex: 1; } .member-pagination { flex-direction: column; align-items: flex-start; } }
@media (max-width: 420px) { .member-toolbar, .member-list-meta, .member-pagination { padding-right: 1rem; padding-left: 1rem; } .member-toolbar-actions { flex-direction: column; align-items: stretch; } }
</style>
